import { Component, Suspense, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { ArrowUpRight, Check, Copy, XIcon } from 'lucide-react';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { HighlightedText } from '@/shared/components/ui/HighlightedText';
import { RippleButton } from '@/shared/components/AnimateUi/RippleButton';
import { useLightAppLogo } from '@/shared/config/branding';
import { changelogIcons } from '@/shared/config/changelog-icons';
import { cn } from '@/shared/utils/utils';
import type { OnboardingChapter, OnboardingSlide } from '@/features/onboarding/types';

const SWIPE_THRESHOLD_PX = 60;
const SLIDE_OFFSET_PX = 48;
const TITLE_CLASS = 'font-serif text-2xl font-semibold leading-tight tracking-tight';
const DESCRIPTION_CLASS = 'text-base leading-relaxed';
/** Keeps RippleButton's ripple inside the button; otherwise it overflows and the dialog shows a scrollbar. */
const CLIP_RIPPLE = 'relative overflow-hidden';

interface OnboardingDialogProps {
  open: boolean;
  chapters: OnboardingChapter[];
  onClose: () => void;
}

export function OnboardingDialog({ open, chapters, onClose }: OnboardingDialogProps) {
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  const nextButtonRef = useRef<HTMLButtonElement>(null);
  const [[index, direction], setPage] = useState<[number, number]>([0, 0]);

  const slides = useMemo(
    () =>
      chapters.flatMap(chapter =>
        chapter.slides.map(slide => ({
          key: slide.id,
          chapter,
          slide,
        }))
      ),
    [chapters]
  );

  useEffect(() => {
    for (const { slide } of slides) slide.preview?.preload();
  }, [slides]);

  const current = slides[index];
  if (!current) return null;

  const { chapter, slide } = current;
  const isFirst = index === 0;
  const isLast = index === slides.length - 1;
  const offset = reduceMotion ? 0 : SLIDE_OFFSET_PX;

  const goTo = (target: number) => {
    if (target < 0 || target >= slides.length || target === index) return;
    setPage([target, target > index ? 1 : -1]);
  };

  const goNext = () => (isLast ? onClose() : goTo(index + 1));
  const goBack = () => goTo(index - 1);

  const closeAndNavigate = (href: string) => {
    onClose();
    navigate({ href });
  };

  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && onClose()}>
      <DialogContent
        hideCloseButton
        className="sm:max-w-xl gap-5 max-h-[calc(100dvh-2rem)] overflow-y-auto overflow-x-hidden"
        onOpenAutoFocus={e => {
          e.preventDefault();
          nextButtonRef.current?.focus();
        }}
        onKeyDown={e => {
          if (e.key === 'ArrowRight') goTo(index + 1);
          if (e.key === 'ArrowLeft') goBack();
        }}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <Badge variant="secondary" className="bg-primary/10 text-primary">
              {chapter.label}
            </Badge>
            {chapter.date && (
              <span className="truncate text-xs text-muted-foreground">{chapter.date}</span>
            )}
          </div>
          <DialogClose asChild>
            <RippleButton
              variant="ghost"
              size="icon"
              className={cn(CLIP_RIPPLE, '-mr-2 h-8 w-8 opacity-70 hover:opacity-100')}
              aria-label="Close tour"
              title="Close tour"
            >
              <XIcon className="h-4 w-4" />
            </RippleButton>
          </DialogClose>
        </div>

        <p className="sr-only" aria-live="polite">
          {`Slide ${index + 1} of ${slides.length}: ${slide.title}`}
        </p>

        <div className="grid grid-cols-1">
          {/* Invisible copies of every slide hold the deck at its tallest slide's height, so the
              buttons never move. They also load every image up front. */}
          {slides.map(item => (
            <div key={item.key} aria-hidden className="invisible col-start-1 row-start-1">
              <SlideBody slide={item.slide} sizer />
            </div>
          ))}
          <PreviewHost slides={slides} index={index} />
          <AnimatePresence mode="wait" initial={false} custom={direction}>
            <motion.div
              key={current.key}
              custom={direction}
              variants={{
                enter: (dir: number) => ({ x: dir * offset, opacity: 0 }),
                center: { x: 0, opacity: 1 },
                exit: (dir: number) => ({ x: dir * -offset, opacity: 0 }),
              }}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.2, ease: 'easeOut' }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.2}
              onDragEnd={(_, info) => {
                if (info.offset.x < -SWIPE_THRESHOLD_PX) goTo(index + 1);
                else if (info.offset.x > SWIPE_THRESHOLD_PX) goBack();
              }}
              className="col-start-1 row-start-1"
            >
              <SlideBody slide={slide} onTryIt={closeAndNavigate} />
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-center">
            {slides.map((item, i) => (
              <button
                key={item.key}
                type="button"
                onClick={() => goTo(i)}
                aria-label={`Go to slide ${i + 1} of ${slides.length}`}
                title={`Go to slide ${i + 1} of ${slides.length}`}
                aria-current={i === index ? 'step' : undefined}
                className="cursor-pointer rounded-full p-1.5 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
              >
                <span
                  className={cn(
                    'block h-2 rounded-full transition-all duration-300',
                    i === index ? 'w-6 bg-primary' : 'w-2 bg-primary/25 hover:bg-primary/40'
                  )}
                />
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between gap-2">
            {isLast ? (
              <Button
                variant="link"
                className="px-0"
                onClick={() => closeAndNavigate('/changelog')}
              >
                See full changelog
              </Button>
            ) : (
              <Button variant="ghost" onClick={onClose}>
                Skip
              </Button>
            )}
            <div className="flex items-center gap-2">
              <RippleButton
                variant="outline"
                className={CLIP_RIPPLE}
                onClick={goBack}
                disabled={isFirst}
              >
                Back
              </RippleButton>
              <RippleButton
                ref={nextButtonRef}
                className={cn(CLIP_RIPPLE, 'min-w-20')}
                onClick={goNext}
              >
                {isLast ? 'Done' : 'Next'}
              </RippleButton>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface DeckSlide {
  key: string;
  slide: OnboardingSlide;
}

function PreviewFallback({ icon }: { icon: OnboardingSlide['icon'] }) {
  const Icon = changelogIcons[icon];
  return (
    <div className="flex size-full items-center justify-center">
      <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-primary/10 text-primary shadow-sm">
        <Icon className="h-10 w-10" />
      </div>
    </div>
  );
}

/** The slide on screen, plus the ones beside it, which is what a Next or Back press shows. */
function neighborIds(slides: DeckSlide[], index: number) {
  const ids: string[] = [];
  for (const position of [index - 1, index, index + 1]) {
    const slide = slides[position]?.slide;
    if (slide?.preview) ids.push(slide.id);
  }
  return ids;
}

/**
 * Paints the upcoming demos out of view, so the one that fades in is already built. The visible
 * slide stays a single layer; these copies never take a place in it.
 */
function PreviewHost({ slides, index }: { slides: DeckSlide[]; index: number }) {
  const activeId = slides[index]?.slide.id;
  const [mounted, setMounted] = useState<Set<string>>(
    () => new Set(neighborIds(slides, index).filter(id => id !== activeId))
  );

  const shownIds = useMemo(() => {
    const ids = new Set(mounted);
    for (const id of neighborIds(slides, index)) {
      if (id !== activeId) ids.add(id);
    }
    return ids;
  }, [mounted, slides, index, activeId]);

  useEffect(() => {
    const ids = slides.flatMap(item =>
      item.slide.preview && item.slide.id !== activeId ? [item.slide.id] : []
    );
    let cursor = 0;
    const timer = window.setInterval(() => {
      const id = ids[cursor];
      cursor += 1;
      if (id) {
        setMounted(prev => (prev.has(id) ? prev : new Set(prev).add(id)));
      }
      if (cursor >= ids.length) window.clearInterval(timer);
    }, 150);
    return () => window.clearInterval(timer);
  }, [slides, activeId]);

  if (shownIds.size === 0) return null;

  return (
    <div
      aria-hidden
      className="pointer-events-none fixed -left-[10000px] top-0 h-56 w-[36rem] overflow-hidden"
    >
      {slides.map(({ slide: item }) =>
        item.preview && shownIds.has(item.id) ? (
          <div key={item.id} inert className="h-56 w-full overflow-hidden">
            <PreviewBoundary fallback={<PreviewFallback icon={item.icon} />}>
              <Suspense fallback={null}>
                <item.preview.Component />
              </Suspense>
            </PreviewBoundary>
          </div>
        ) : null
      )}
    </div>
  );
}

interface SlideBodyProps {
  slide: OnboardingSlide;
  /**
   * Renders plain tags for the invisible sizing copies, since the dialog's title and
   * description must be unique.
   */
  sizer?: boolean;
  onTryIt?: (href: string) => void;
}

function SlideBody({ slide, sizer = false, onTryIt }: SlideBodyProps) {
  const appLogo = useLightAppLogo();
  const Icon = changelogIcons[slide.icon];
  const { link, copy, preview } = slide;
  const icon = (
    <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-primary/10 text-primary shadow-sm">
      <Icon className="h-10 w-10" />
    </div>
  );

  return (
    <div className="space-y-5">
      {preview ? (
        <div inert className="h-44 sm:h-56 overflow-hidden rounded-xl border bg-background">
          {!sizer && (
            <PreviewBoundary fallback={<PreviewFallback icon={slide.icon} />}>
              <Suspense fallback={null}>
                <preview.Component />
              </Suspense>
            </PreviewBoundary>
          )}
        </div>
      ) : (
        <div
          className={cn(
            'flex h-44 sm:h-56 items-center justify-center overflow-hidden rounded-xl border p-4',
            slide.image
              ? 'bg-white dark:bg-slate-100'
              : 'bg-gradient-to-br from-primary/15 via-primary/5 to-transparent'
          )}
        >
          {slide.image ? (
            <img
              src={appLogo}
              alt=""
              draggable={false}
              className="max-h-full max-w-full rounded-md object-contain"
            />
          ) : (
            icon
          )}
        </div>
      )}

      <div className="space-y-2">
        {sizer ? (
          <>
            <h2 className={TITLE_CLASS}>{slide.title}</h2>
            <p className={DESCRIPTION_CLASS}>
              <HighlightedText text={slide.description} />
            </p>
          </>
        ) : (
          <>
            <DialogTitle className={TITLE_CLASS}>{slide.title}</DialogTitle>
            <DialogDescription className={DESCRIPTION_CLASS}>
              <HighlightedText text={slide.description} />
            </DialogDescription>
          </>
        )}
      </div>

      {(link || copy) && (
        <div className="flex flex-wrap gap-2">
          {link && (
            <RippleButton
              variant="outline"
              size="sm"
              className={CLIP_RIPPLE}
              onClick={() => onTryIt?.(link)}
            >
              Try it
              <ArrowUpRight className="h-3 w-3" />
            </RippleButton>
          )}
          {copy && <CopyButton label={copy.label} text={copy.text} />}
        </div>
      )}
    </div>
  );
}

/**
 * The deck is mounted at the app root, so a preview that fails to load or render would otherwise
 * take the whole app down.
 */
class PreviewBoundary extends Component<
  { fallback: ReactNode; children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

function CopyButton({ label, text }: NonNullable<OnboardingSlide['copy']>) {
  const [copied, setCopied] = useState(false);

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  return (
    <RippleButton variant="outline" size="sm" className={CLIP_RIPPLE} onClick={copyText}>
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      {copied ? 'Copied' : label}
    </RippleButton>
  );
}
